import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX } from 'react';
import type { CopyableTeamscaleLinkProps } from 'ts/base/components/CollapsingTeamscaleLink';
import { CopyableTeamscaleLink } from 'ts/base/components/CollapsingTeamscaleLink';
import { EnumUtils } from 'ts/commons/EnumUtils';
import { Links } from 'ts/commons/links/Links';
import { StringUtils } from 'ts/commons/StringUtils';
import type { SpecItemFinding, SpecItemFindingLocation } from 'ts/perspectives/findings/FindingsUtils';
import { EWorkItemType } from 'typedefs/EWorkItemType';
import type { ManualTestCaseTextRegionLocation } from 'typedefs/ManualTestCaseTextRegionLocation';
import type { SpecItem } from 'typedefs/SpecItem';
import type { TeamscaleIssue } from 'typedefs/TeamscaleIssue';
import type { TeamscaleIssueFieldLocation } from 'typedefs/TeamscaleIssueFieldLocation';
import type { TestStepCellIdentifier } from 'typedefs/TestStepCellIdentifier';

/** Props for FindingBasedSpecItemLink */
export type FindingBasedSpecItemLinkProps = Omit<CopyableTeamscaleLinkProps, 'persistentPath' | 'to'> & {
	projectId: string;
	commit: UnresolvedCommitDescriptor | undefined;
	findingLocation: SpecItemFinding['location'];
	specItem: SpecItem;
};

/** A link to a spec item with a descriptive name based on a finding. */
export function FindingBasedSpecItemLink({
	projectId,
	commit,
	specItem,
	findingLocation,
	...linkProps
}: FindingBasedSpecItemLinkProps): JSX.Element {
	const issueId = findingLocation.issueId;
	const displayMessage = getSpecItemFindingDisplayMessage(specItem, findingLocation);
	return (
		<CopyableTeamscaleLink
			persistentPath={displayMessage}
			to={Links.specItem(projectId, issueId, { commit })}
			{...linkProps}
		/>
	);
}

/** Resolves the human readable type of the provided item */
export function resolveType(workItem: TeamscaleIssue) {
	let type;
	if ('typeInfo' in workItem) {
		type = (workItem as SpecItem).typeInfo?.type;
	}
	return type ?? EnumUtils.fromValue(workItem.workItemType, EWorkItemType, EWorkItemType.SPEC_ITEM).humanReadable;
}

/** Returns the display text for a Spec item finding location, e.g. "Test Case 3 (requirements1), Action of Step 6". */
export function getSpecItemFindingDisplayMessage(specItem: SpecItem, location: SpecItemFinding['location']): string {
	const isMultiStepFinding =
		isManualTestCaseRegionLocation(location) &&
		location.startTestStepIdentifier.index < location.endTestStepIdentifier.index;

	// Test Case 3 (requirements1)
	let displayMessage = `${resolveType(specItem)} ${specItem.id.externalId} (${specItem.id.connectorId})`;

	if (isManualTestCaseRegionLocation(location)) {
		// , Action of Step 6
		displayMessage += `, ${prettyPrintStepIdentifier(location.startTestStepIdentifier)}`;

		if (isMultiStepFinding) {
			// - Check of Step 7
			displayMessage += ` - ${prettyPrintStepIdentifier(location.endTestStepIdentifier)}`;
		}
	} else if (isTeamscaleIssueFieldLocation(location)) {
		// , Description
		displayMessage += `, ${StringUtils.camelCaseToNormalCase(location.affectedField)}`;
	}

	return displayMessage;
}

function isManualTestCaseRegionLocation(
	location: SpecItemFindingLocation
): location is ManualTestCaseTextRegionLocation {
	return 'startTestStepIdentifier' in location;
}

function isTeamscaleIssueFieldLocation(location: SpecItemFindingLocation): location is TeamscaleIssueFieldLocation {
	return 'fieldStartOffset' in location;
}

function prettyPrintStepIdentifier(stepIdentifier: TestStepCellIdentifier): string {
	return `${stepIdentifier.isAction ? 'Action' : 'Check'} of Step ${
		// increment to handle zero-based offset for test steps
		stepIdentifier.index + 1
	}`;
}
